import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthHeader } from '@/hooks/useAuth';
import { apiErrorMessage } from '@/lib/api/client';
import { getWarehouseTree } from '@/lib/api/warehouse';
import { getProductGroupTypes } from '@/lib/api/product-group-type';
import { getProductGroupTreesExtended } from '@/lib/api/product-group-tree';
import {
  getLatestStoreSkuSalesDatesDefaults,
  previewStoreSkuSalesDatesRowCount,
  startStoreSkuSalesDatesExtraction,
} from '@/lib/api/analysis-data-extraction';
import type { ProductGroupTreeExtended, ProductGroupType, WarehouseTreeNode } from '@/lib/types';
import type {
  ProductGroupDepthSelection,
  StoreSkuSalesDatesDefaults,
} from '@/lib/types/feature/analysis-data-extraction';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { WarehouseQuickSelect } from './WarehouseQuickSelect';
import { WarehouseMultiSelect } from './WarehouseMultiSelect';
import { PreviewRowCountButton } from './PreviewRowCountButton';
import { ProductGroupTreeMultiSelect } from './ProductGroupTreeMultiSelect';
import {
  buildProductGroupTree,
  buildSelections,
  expandSelectionsToLeafKeys,
} from '@/lib/utils/product-group-tree';

interface StoreSkuSalesDatesFormProps {
  account: { id: string; code: string; name: string };
  onSubmitted?: () => void;
}

/**
 * 店舗×SKU 初回/最終販売日・初回入庫日の実行フォーム（FKV3-12941 / 初回入庫日は FKV3-13065）
 *
 * 対象倉庫と対象SKU群（商品グループ値）はどちらも必須。設定値リストと同じく、
 * 母集団が店舗×SKUの全グリッドになるため「未指定＝全件」は用意しない。
 */
export function StoreSkuSalesDatesForm({ account, onSubmitted }: StoreSkuSalesDatesFormProps) {
  const { getAuthHeader } = useAuthHeader();

  const [warehouseNodes, setWarehouseNodes] = useState<WarehouseTreeNode[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<Set<string>>(new Set());
  const warehouseIds = useMemo(
    () => warehouseNodes.filter((n) => n.node_type === 'warehouse').map((n) => n.id),
    [warehouseNodes],
  );
  // 前回実行条件から復元した倉庫が削除されている場合があるため、現存する倉庫だけに絞る
  // （検証も送信もこの値だけを見る。StoreSkuSettingValuesForm と同じ理由）
  const effectiveWarehouseIds = useMemo(
    () => warehouseIds.filter((id) => selectedWarehouseIds.has(id)),
    [warehouseIds, selectedWarehouseIds],
  );

  const [types, setTypes] = useState<ProductGroupType[]>([]);
  const [trees, setTrees] = useState<ProductGroupTreeExtended[]>([]);
  const [typeId, setTypeId] = useState<string>('');
  // ツリーで選んだ末端ノードのキー（送信時に「配下が全選択の親」へ畳む）
  const [selectedLeafKeys, setSelectedLeafKeys] = useState<Set<string>>(new Set());
  // 前回実行条件の商品グループ。ツリーが組み上がってから選択状態へ展開する
  const [pendingSelections, setPendingSelections] = useState<ProductGroupDepthSelection[] | null>(
    null,
  );

  const [includeSkusWithoutSales, setIncludeSkusWithoutSales] = useState(false);
  const [compressAsZip, setCompressAsZip] = useState(false);

  const [pendingDefaults, setPendingDefaults] = useState<
    StoreSkuSalesDatesDefaults | null | undefined
  >(undefined);
  const [defaultsApplied, setDefaultsApplied] = useState(false);

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mastersLoading, setMastersLoading] = useState(() => Boolean(account.code));

  // アカウント切替時に前アカウントの入力・選択を持ち越さないようリセットする
  // （レンダー中の調整パターン。理由は StoreSkuSettingValuesForm と同じ）
  const prevAccountCodeRef = useRef(account.code);
  if (account.code !== prevAccountCodeRef.current) {
    prevAccountCodeRef.current = account.code;
    setWarehouseNodes([]);
    setSelectedWarehouseIds(new Set());
    setTypes([]);
    setTrees([]);
    setTypeId('');
    setSelectedLeafKeys(new Set());
    setPendingSelections(null);
    setIncludeSkusWithoutSales(false);
    setCompressAsZip(false);
    setPendingDefaults(undefined);
    setDefaultsApplied(false);
    setError(null);
    setMastersLoading(true);
  }

  // マスタ（倉庫ツリー・商品グループタイプ・商品階層）と前回実行条件の取得
  useEffect(() => {
    if (!account.code) return;
    let cancelled = false;
    const authHeaders = getAuthHeader();

    const treePromise = getWarehouseTree(account.code, authHeaders)
      .then((nodes) => {
        if (!cancelled) setWarehouseNodes(nodes);
      })
      .catch(() => {
        if (!cancelled) setError('倉庫一覧の取得に失敗しました');
      });
    const typesPromise = getProductGroupTypes(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setTypes(res);
      })
      .catch(() => {
        if (!cancelled) setError('商品グループタイプの取得に失敗しました');
      });
    const groupsPromise = getProductGroupTreesExtended(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setTrees(res);
      })
      .catch(() => {
        if (!cancelled) setError('商品階層の取得に失敗しました');
      });
    const defaultsPromise = getLatestStoreSkuSalesDatesDefaults(account.code, authHeaders)
      .then((res) => {
        if (!cancelled) setPendingDefaults(res.has_previous_execution ? res.parameters : null);
      })
      .catch(() => {
        if (!cancelled) setPendingDefaults(null);
      });

    Promise.allSettled([treePromise, typesPromise, groupsPromise, defaultsPromise]).then(() => {
      if (!cancelled) setMastersLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [account.code]);

  // 前回実行条件をフォームへ反映（アカウント切替ごとに1回のみ）
  useEffect(() => {
    if (defaultsApplied || pendingDefaults === undefined) return;
    if (pendingDefaults) {
      setTypeId(pendingDefaults.product_group_type_id);
      setPendingSelections(pendingDefaults.product_group_selections ?? null);
      setSelectedWarehouseIds(new Set(pendingDefaults.target_warehouse_ids));
      setIncludeSkusWithoutSales(pendingDefaults.include_skus_without_sales ?? false);
      setCompressAsZip(pendingDefaults.compress_as_zip ?? false);
    }
    setDefaultsApplied(true);
  }, [pendingDefaults, defaultsApplied]);

  // タイプ未選択なら先頭のタイプを既定にする（前回実行条件の反映後に走らせる）
  useEffect(() => {
    if (!defaultsApplied || typeId || types.length === 0) return;
    setTypeId(types[0].id);
  }, [defaultsApplied, typeId, types]);

  // 選択中のタイプの商品階層ツリー（プレオーダー平坦リスト）
  const treeNodes = useMemo(() => buildProductGroupTree(trees, typeId), [trees, typeId]);

  // 前回実行条件の商品グループは、ツリーが組み上がってから選択状態へ展開する
  // （親が指定されていれば配下の末端をすべて選択済みにする）
  useEffect(() => {
    if (!pendingSelections || treeNodes.length === 0) return;
    setSelectedLeafKeys(expandSelectionsToLeafKeys(treeNodes, pendingSelections));
    setPendingSelections(null);
  }, [pendingSelections, treeNodes]);

  // ツリーに無いノードが選ばれたままにならないよう、実際に送る値はツリーから組み立てる
  // （タイプを変えた直後や、前回実行条件の復元後にマスタが変わっていた場合）
  const effectiveSelections = useMemo(
    () => buildSelections(treeNodes, selectedLeafKeys),
    [treeNodes, selectedLeafKeys],
  );

  const changeTypeId = (value: string) => {
    setTypeId(value);
    setSelectedLeafKeys(new Set());
    setPendingSelections(null);
  };

  const validate = (): string | null => {
    if (effectiveWarehouseIds.length === 0) return '対象倉庫を1つ以上選択してください';
    if (!typeId) return '対象SKU群の商品グループタイプを選択してください';
    if (effectiveSelections.length === 0) return '対象SKU群の商品グループを1つ以上選択してください';
    return null;
  };

  // 件数プレビューと実行で同じ条件を送る（片方だけ直して食い違うのを防ぐ）
  const buildRequest = () => ({
    target_warehouse_ids: effectiveWarehouseIds,
    product_group_type_id: typeId,
    product_group_selections: effectiveSelections,
    include_skus_without_sales: includeSkusWithoutSales,
    compress_as_zip: compressAsZip,
  });

  const handlePreviewRowCount = async () => {
    const validationError = validate();
    if (validationError) throw new Error(validationError);
    try {
      const res = await previewStoreSkuSalesDatesRowCount(
        account.code,
        buildRequest(),
        getAuthHeader(),
      );
      return res.row_count;
    } catch (e) {
      // 件数の確認は条件が広いと時間内に終わらない。サーバーが返す案内をそのまま見せる
      throw new Error(apiErrorMessage(e, '件数の取得に失敗しました'));
    }
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    try {
      setSubmitting(true);
      setError(null);
      const authHeaders = getAuthHeader();
      await startStoreSkuSalesDatesExtraction(account.code, buildRequest(), authHeaders);
      toast.success('抽出ジョブを受け付けました。実行ログで進捗を確認できます');
      onSubmitted?.();
    } catch (e) {
      const message = e instanceof Error ? e.message : '抽出ジョブの実行に失敗しました';
      setError(message);
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>抽出条件</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* 出力する値の定義。商品マスタ抽出の同名の列と判定を揃えている */}
        <p className="text-sm text-muted-foreground">
          1行＝店舗×SKUで、その店舗でそのSKUが最初に売れた日（販売開始日）・最後に売れた日（最終販売日）と、
          その店舗の倉庫にそのSKUが最初に入った日（初回入庫日）・そのときの在庫数（初回入庫数）を出力します。
          期間の指定はありません（全期間が対象）。販売日は販売数が正の日で判定し、返品しかない日は販売とみなしません。
          在庫は倉庫が持つものなので、初回入庫日は店舗ではなく倉庫ごとの値になります。
        </p>

        {/* 対象倉庫（必須） */}
        <div className="space-y-2">
          <Label>対象倉庫（必須。1つ以上選択してください）</Label>
          <p className="text-sm text-muted-foreground">
            選んだ倉庫に紐づく店舗を出力対象にします。
            1つの店舗が複数の対象倉庫に紐づく場合は、倉庫の数だけ同じ店舗の行が出ます（日付は同じ値）。
          </p>
          <WarehouseQuickSelect
            accountCode={account.code}
            selectedIds={selectedWarehouseIds}
            onChange={setSelectedWarehouseIds}
            idPrefix="sales-dates-wh"
            disabled={mastersLoading}
          />
          <WarehouseMultiSelect
            nodes={warehouseNodes}
            selectedIds={selectedWarehouseIds}
            onChange={setSelectedWarehouseIds}
            idPrefix="sales-dates-wh"
            loading={mastersLoading}
          />
        </div>

        {/* 対象SKU群（必須） */}
        <div className="space-y-2">
          <Label>対象SKU群（必須。商品グループで指定します）</Label>
          <p className="text-sm text-muted-foreground">
            全SKUを出すと店舗×SKUの全グリッド（アカウントによっては100万行規模）になるため、
            商品グループでの絞り込みを必須にしています。上位階層を選ぶと、その配下すべてが対象になります。
          </p>
          <div className="flex items-center gap-2">
            <Label htmlFor="sales-dates-type">商品グループタイプ</Label>
            <Select value={typeId} onValueChange={changeTypeId} disabled={mastersLoading}>
              <SelectTrigger id="sales-dates-type" className="w-56">
                <SelectValue placeholder="タイプを選択" />
              </SelectTrigger>
              <SelectContent>
                {types.map((type) => (
                  <SelectItem key={type.id} value={type.id}>
                    {type.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <ProductGroupTreeMultiSelect
            nodes={treeNodes}
            selectedLeafKeys={selectedLeafKeys}
            onChange={setSelectedLeafKeys}
            idPrefix="sales-dates-pg"
            loading={mastersLoading}
            emptyMessage="選択したタイプに商品グループがありません"
          />
        </div>

        {/* 実績のない組み合わせの扱い */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id="sales-dates-include-no-sales"
              checked={includeSkusWithoutSales}
              onCheckedChange={(checked) => setIncludeSkusWithoutSales(checked === true)}
              disabled={mastersLoading}
            />
            <Label htmlFor="sales-dates-include-no-sales" className="font-normal">
              実績のない店舗×SKUも出力する
            </Label>
          </div>
          <p className="text-sm text-gray-500">
            ONにすると、選んだ倉庫の店舗×対象SKUをすべて出力し、実績のない組み合わせは日付が空欄になります。
            OFF（既定）なら販売または入庫の実績がある組み合わせだけを出力します。
          </p>
        </div>

        {/* ZIP圧縮 */}
        <div className="flex items-center gap-2">
          <Checkbox
            id="sales-dates-zip"
            checked={compressAsZip}
            onCheckedChange={(checked) => setCompressAsZip(checked === true)}
            disabled={mastersLoading}
          />
          <Label htmlFor="sales-dates-zip" className="font-normal">
            ZIPに圧縮して出力する
          </Label>
        </div>

        <div className="flex items-center justify-between gap-4">
          {/* 実行前に件数を確かめられるようにする（0件のまま実行して空のCSVを受け取らないため） */}
          <PreviewRowCountButton
            onFetch={handlePreviewRowCount}
            disabled={mastersLoading || submitting}
            conditionKey={JSON.stringify(buildRequest())}
          />
          <Button onClick={handleSubmit} disabled={submitting || mastersLoading}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            抽出を実行
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
